import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Button,
  Chip
} from '@mui/material';
import {
  Check as AcceptIcon,
  Close as DeclineIcon,
  PersonAdd as InviteIcon
} from '@mui/icons-material';
import { useProfile } from '../contexts/ProfileContext';
import inviteService from '../services/inviteService';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import InviteAcceptDialog from './InviteAcceptDialog';
import logger from '../utils/logger';

const PendingInvites = () => {
  const { profile } = useProfile();
  const navigate = useNavigate();

  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadInvites = useCallback(async () => {
    if (!profile?.email) return;

    try {
      setLoading(true);
      setError('');

      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        const pendingInvites = await inviteService.getInvitesByEmail(profile.email);
        setInvites(pendingInvites.filter(invite => invite.status === 'pending'));
      } else {
        setError('You must be logged in to view invites');
      }
    } catch (err) {
      logger.error('Error loading invites:', err);
      setError('Failed to load invites: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.email]);

  useEffect(() => {
    loadInvites();
  }, [loadInvites]);

  // Open the accept dialog instead of redirecting to /signup
  const handleAccept = (invite) => {
    setSelectedInvite(invite);
    setDialogOpen(true);
  };

  // Handle successful join from the dialog
  const handleJoinSuccess = (newFamilyCode) => {
    setDialogOpen(false);
    setSelectedInvite(null);
    setSuccess(`You've joined ${newFamilyCode}! Redirecting...`);

    // Remove the accepted invite from the list
    if (selectedInvite) {
      setInvites(prev => prev.filter(i => i.id !== selectedInvite.id));
    }

    // Navigate to home after a short delay so user sees the success message
    setTimeout(() => {
      navigate('/');
    }, 1500);
  };

  // Handle dialog close (declined or dismissed)
  const handleDialogClose = (reason) => {
    setDialogOpen(false);

    if (reason === 'declined' && selectedInvite) {
      setInvites(prev => prev.filter(i => i.id !== selectedInvite.id));
      setSuccess('Invitation declined.');
    }

    setSelectedInvite(null);
  };

  // Direct decline without opening dialog
  const handleDecline = async (inviteId) => {
    try {
      setLoading(true);
      setError('');
      await inviteService.declineInvite(inviteId);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      setSuccess('Invitation declined.');
    } catch (err) {
      logger.error('Error declining invite:', err);
      setError('Failed to decline invite');
    } finally {
      setLoading(false);
    }
  };

  if (!profile?.email) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2} py={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Checking for invitations...</Typography>
      </Box>
    );
  }

  if (invites.length === 0 && !error && !success) {
    return null;
  }

  return (
    <>
      <Paper sx={{ mt: 2, p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <InviteIcon color="primary" />
          <Typography variant="h6">
            Pending Invitations
          </Typography>
          <Chip label={invites.length} size="small" color="primary" />
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <List disablePadding>
          {invites.map((invite) => (
            <ListItem
              key={invite.id}
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                mb: 1,
                '&:last-child': { mb: 0 }
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle2">
                    Invitation to join <strong>{invite.familyCode}</strong>
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    From: {invite.inviterName || invite.inviterEmail}
                    {invite.relationship && ` (as ${invite.relationship})`}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AcceptIcon />}
                  onClick={() => handleAccept(invite)}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  Accept
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<DeclineIcon />}
                  onClick={() => handleDecline(invite.id)}
                  disabled={loading}
                >
                  Decline
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Accept dialog — handles leave current family + join new family */}
      <InviteAcceptDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        invite={selectedInvite}
        onSuccess={handleJoinSuccess}
      />
    </>
  );
};

export default PendingInvites;
